/**
 * Every mark the Stocks section draws, and **all of them are drawn** rather than typed.
 *
 * 🔴 **F-D66-1: Geist has no `⚠`, `✓`, `✗` or `⧗`.** Measured in the running app, not assumed —
 * `FleetMarks.tsx` carries the measurement. A typed check comes from whatever the operating
 * system substitutes, which on this box is Segoe UI Symbol, on a Mac is Apple Symbols and on a
 * bare Linux box is a tofu box. `API-and-UI.md` restates the rule for this section specifically:
 * *"Any state glyph in this section is drawn."*
 *
 * ## The verdict marks carry no colour, and that is the section's load-bearing rule
 *
 * `BUY` is not green and `SELL` is not red. Green and red are spent on profit and loss, and a red
 * `SELL` beside a red `−12.0%` would put two meanings in one colour in the one row where a misread
 * costs money. So each verdict is carried four times over — by its **position** under a section
 * header, by the **type weight** of that header, by the **word** spelled out in full, and by a
 * drawn **glyph** here. Colour is not one of the four.
 *
 * The four glyphs share one 16-unit cube so they read as a set: `BUY` shows the cube's own edges,
 * `SELL` strikes it through, `REVIEW` is an hourglass (the decision has not been made), and `HOLD`
 * is the cube with a single level line. They differ in *stroke*, not in weight or fill, so no
 * substitution and no size can collapse one into another.
 */

import type { JSX } from 'react';

import type { CoverageTier, Verdict } from './model';

/** One 16×16 stroked frame, so every mark in the section sits on the same optical grid. */
function Mark({
  size = 16,
  title,
  children,
}: {
  size?: number;
  title?: string;
  children: JSX.Element | JSX.Element[];
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      // `aria-hidden` unless the caller names it: in every use here the word is beside the mark,
      // and a screen reader announcing "BUY graphic BUY" is worse than one that reads it once.
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title}
      style={{ flex: '0 0 auto' }}
    >
      {children}
    </svg>
  );
}

const CUBE = 'M8 1.6l5.6 3.2v6.4L8 14.4 2.4 11.2V4.8z';

export function VerdictMark({ verdict, size = 17 }: { verdict: Verdict; size?: number }): JSX.Element {
  switch (verdict) {
    case 'BUY':
      return (
        <Mark size={size}>
          <path d={CUBE} />
          <path d="M8 1.6v12.8M2.4 4.8L8 8l5.6-3.2" />
        </Mark>
      );
    case 'SELL':
      return (
        <Mark size={size}>
          <path d={CUBE} />
          <path d="M5.4 5.4l5.2 5.2M10.6 5.4l-5.2 5.2" />
        </Mark>
      );
    case 'REVIEW':
      // An hourglass, not a question mark: `REVIEW` means the decision is still running, and a
      // question mark reads as "we do not know" — which is `no verdict`, a different state.
      return (
        <Mark size={size}>
          <path d="M4 1.8h8M4 14.2h8M4.6 1.8v3.1L8 8l3.4-3.1V1.8M4.6 14.2v-3.1L8 8l3.4 3.1v3.1" />
        </Mark>
      );
    case 'HOLD':
      return (
        <Mark size={size}>
          <path d="M2.4 8h11.2" />
          <path d={CUBE} />
        </Mark>
      );
  }
}

/** A rule's outcome. The only two states a rule has; there is no third. */
export function RuleMark({ passed, size = 13 }: { passed: boolean; size?: number }): JSX.Element {
  return (
    <Mark size={size} title={passed ? 'passes' : 'fails'}>
      <path
        d={passed ? 'M3 8.4l3.2 3.2L13 4.8' : 'M4 4l8 8M12 4l-8 8'}
        strokeWidth={1.8}
      />
    </Mark>
  );
}

/** The caution triangle. Amber lives on the *rail beside* it, never inside the type. */
export function CautionMark({ size = 14 }: { size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="var(--attn)"
      strokeWidth={1.6}
      aria-hidden="true"
      style={{ flex: '0 0 auto', marginTop: 2 }}
    >
      <path d="M8 2.2l6 10.6H2z" />
      <path d="M8 6.4v3M8 11.1v.1" />
    </svg>
  );
}

/** The disclosure chevron. Rotated by CSS on the open state, so the rotation can be animated. */
export function Chevron({ size = 13 }: { size?: number }): JSX.Element {
  return (
    <Mark size={size}>
      <path d="M3.5 6l4.5 4.5L12.5 6" />
    </Mark>
  );
}

/**
 * Coverage tier: the word **and** a three-segment fill.
 *
 * 🔴 **Never green/amber/red.** `API-and-UI.md` is explicit — those three already mean profit,
 * loss and real money, and a tier is none of them. The segments are `currentColor`, so the chip
 * inherits whatever ink it sits in, and the *count* of filled segments is the signal.
 */
export function TierMark({ tier }: { tier: CoverageTier }): JSX.Element {
  const filled = tier === 'FULL' ? 3 : tier === 'PRICE' ? 2 : 1;
  return (
    <span className="jmt-stk-tier" title={`${tier} coverage tier`}>
      {tier}
      <span aria-hidden="true" className="jmt-stk-tier__bars">
        {[0, 1, 2].map((i) => (
          <span key={i} data-on={i < filled ? '' : undefined} />
        ))}
      </span>
    </span>
  );
}

/**
 * `BROKER · READ-ONLY`.
 *
 * 🔴 **A third mode marker, in geometry rather than a new colour.** The product's mode vocabulary
 * has exactly two states — amber for live real money, blue dashed for paper — and the Stocks
 * section is neither. Amber would say *this system can act with real money here*, and it cannot:
 * there is no order path and no code that could serve one. Blue would say *this money is
 * fictional*, and Trading 212 holdings are real. So the marker is a **dotted** square against
 * amber's solid and paper's dashed, its ink is neutral, and the words carry the meaning.
 */
export function BrokerPill({ short = false }: { short?: boolean }): JSX.Element {
  return (
    <span className="jmt-stk-broker" title="Brokerage holdings are real money this system cannot touch">
      <span aria-hidden="true" className="jmt-stk-broker__mark" />
      {short ? 'BROKER' : 'BROKER · READ-ONLY'}
    </span>
  );
}

/**
 * The four families as they read on a card. ⚠ The order is fixed and matches `rank.WEIGHTS`, so a
 * reader learns one position per family instead of relearning the layout on every row.
 */
const FAMILIES: readonly { key: string; label: string; abbr: string }[] = [
  { key: 'valuation', label: 'Value', abbr: 'V' },
  { key: 'quality', label: 'Quality', abbr: 'Q' },
  { key: 'market', label: 'Trend', abbr: 'T' },
  { key: 'flow', label: 'Flow', abbr: 'F' },
];

/** How many marks a family gets. Four steps: a fifth would imply precision the score does not have. */
const STEPS = 4;

/**
 * Per-family strength, drawn as four marks each.
 *
 * 🔴 **Marks, not digits, and no colour.** The three colour laws are product truth: `--attn` means
 * money at risk, `--profit`/`--loss` mean P&L, `--accent` means interactive. A sub-score is none of
 * those, so this is built entirely from the text ramp — filled marks in `--text-secondary`, empty
 * ones in a hairline. Printing `0.94` instead would be the false precision the design refuses, and
 * printing four of them would be that mistake in the plural.
 *
 * ⚠ **A family with no inputs renders as unmeasured, not as zero.** A missing gross margin is not a
 * mediocre gross margin — `rank.score` drops absent families and renormalises rather than scoring
 * them, and a row of empty marks would say the opposite of what the data says.
 */
export function FamilyBars({
  families,
  covered,
}: {
  families: Record<string, number>;
  covered?: number;
}): JSX.Element {
  const thin = covered !== undefined && covered < 0.5;
  return (
    <span className="jmt-stk-fam" role="list">
      {FAMILIES.map(({ key, label, abbr }) => {
        const value = families[key];
        const measured = typeof value === 'number';
        // `round` rather than `ceil`, with a floor of 1 so a family that contributed something
        // never renders identically to one that contributed nothing.
        //
        // ⚠ **Measured, and the honest finding is that neither choice helps on a BUY list.** With
        // `ceil` the 32 rendered rows produced four distinct patterns with nineteen alike; with
        // `round`, four patterns with twenty-six alike. A BUY is by construction a name that scored
        // well on every family, so the sub-scores are compressed near the top and no mapping to
        // four marks can separate them. That is why this component was taken **off** the screener
        // row and kept only on the Company page, where nothing is being compared and what matters
        // is spotting a weak or unmeasured family rather than out-ranking a neighbour.
        const filled = measured ? Math.max(1, Math.round(value * STEPS)) : 0;
        return (
          <span
            className="jmt-stk-fam__item"
            key={key}
            role="listitem"
            data-unmeasured={measured ? undefined : ''}
            // The number is in the accessible name and nowhere on screen. A screen reader gets the
            // measurement; the eye gets the comparison, which is what a card is for.
            aria-label={
              measured
                ? `${label}: ${Math.round(value * 100)} of 100`
                : `${label}: not measurable for this company`
            }
          >
            {/* 🔴 Both rendered, one shown — and hiding the label outright was the first
                attempt. Measured at 390px: the four groups collapsed to a 3px-tall row of
                unlabelled 7px bars, so a reader could not tell which family was which and the
                component became decoration. An initial is the smallest thing that stays
                identifiable, and the accessible name keeps the full word either way. */}
            <span className="jmt-stk-fam__label">{label}</span>
            <span className="jmt-stk-fam__abbr" aria-hidden="true">
              {abbr}
            </span>
            <span className="jmt-stk-fam__marks" aria-hidden="true">
              {Array.from({ length: STEPS }, (_, index) => (
                <span className="jmt-stk-fam__mark" key={index} data-on={index < filled ? '' : undefined} />
              ))}
            </span>
          </span>
        );
      })}
      {/* ⚠ Said on the row rather than left to the expansion. A score resting on under half the
          intended weight is not comparable with a fully measured one, and the reader is about to
          compare them in a ranked list. */}
      {thin ? <span className="jmt-stk-fam__thin">thin coverage</span> : null}
    </span>
  );
}
